import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import ReportService from '../../services/ReportService';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { PaymentsByLoyaltyDto, PaymentsByLoyaltyRequest } from "@ontime/types";
import { DownloadExcel } from './ReportDialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSelector } from 'react-redux';
import { selectUserOrganizationsList } from '../../stateManagement/organizationUserSlice';
import { Controller, useForm } from 'react-hook-form';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import dayjs from 'dayjs';
import InfoComponent from '../../customComponents/InfoComponent';

const PaymentsByLoyalty: React.FC<{
    loading: boolean;
    setLoading: (input: boolean) => void;
}> = ({ loading, setLoading }) => {

    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<PaymentsByLoyaltyDto[]>([]);

    const GetPaymentsByLoyalty = async (filter: PaymentsByLoyaltyRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetPaymentsByLoyalty(filter);
            setData(response.data.result);
        } catch (error) {
            enqueueSnackbar('Failed to fetch place', { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const GetPaymentsByLoyaltyExcel = async (filter: PaymentsByLoyaltyRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetPaymentsByLoyaltyExcel(filter);
            DownloadExcel('PaymentsByLoyalty', response.data);
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Failed to fetch Report', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const organizationList = useSelector(selectUserOrganizationsList);

    const AppointmentByClientIdSchema = z.object({
        organizationId: z.string(),
        creationTimeFrom: z.string().optional(),
        creationTimeTo: z.string().optional(),
    });

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<PaymentsByLoyaltyRequest>({
        resolver: zodResolver(AppointmentByClientIdSchema),
        defaultValues: {
            organizationId: organizationList?.[0].organizationId ?? ''
        },
    });

    const search = (
        <Box gap={2} m={2}>
            <form onSubmit={handleSubmit(GetPaymentsByLoyalty)} noValidate>
                <InfoComponent infoContent='Report filters'>
                    <Box display="flex" justifyContent="space-evenly" flexWrap='wrap' gap={2} mt={2}>
                        <FormControl sx={{ minWidth: '190px' }} error={!!errors.organizationId} disabled={loading}>
                            <InputLabel>Place</InputLabel>
                            <Controller
                                name="organizationId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Place"
                                        {...field}
                                    >
                                        {organizationList.map((item) => (
                                            <MenuItem key={item.organizationId} value={item.organizationId}>
                                                {item.organizationName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{errors.organizationId?.message}</FormHelperText>
                        </FormControl>

                        <Controller
                            name="creationTimeFrom"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="From"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                        <Controller
                            name="creationTimeTo"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="To"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                    </Box>
                </InfoComponent>
                <CustomFormAction>
                    <CustomButton lable='Show Report' type='submit' disabled={loading} infoContent={`Click to show report`} />
                    <CustomButton lable='Download Excel' onClick={handleSubmit(GetPaymentsByLoyaltyExcel)} disabled={loading} infoContent={`Click to download excel`} />
                </CustomFormAction>
            </form>
        </Box>
    )

    const paymentsByLoyaltyType: GridColumnType<PaymentsByLoyaltyDto> = {
        paymentDate: { type: 'Text', header: 'Payment Date' },
        paymentTime: { type: 'Text', header: 'Payment Time' },
        paymentUsedPointsCount: { type: 'Integer', header: 'Used Points' },
        paymentReductionAmountByUsedPoints: { type: 'Price', header: 'Reduction by Points' },
        client: { type: 'Text', header: 'Client Name' },
        phoneNumber: { type: 'Text', header: 'Client Phone' },
    };

    return (
        <GridTable<PaymentsByLoyaltyDto, PaymentsByLoyaltyRequest, PaymentsByLoyaltyDto>
            gridTitle='Payments By Loyalty Report'
            list={data}
            inputType={paymentsByLoyaltyType}
            customSearch={search}
        />
    );
};

export default PaymentsByLoyalty;
